import supabase from '../config/supabase';
import config from '../config/environment';
import type { CycleObservations, FupStoredAnswers } from '../types/cycleObservations';
import type { StudentNoteItem } from '../types/orchestrator';

export type WorkoutAssignmentDateField =
  | 'previous_training_date'
  | 'current_training_date'
  | 'expected_delivery_date'
  | 'delivered_date';

export class WorkoutAssignmentRequestError extends Error {
  readonly fields: Partial<Record<WorkoutAssignmentDateField, string>>;

  constructor(message: string, fields: Partial<Record<WorkoutAssignmentDateField, string>> = {}) {
    super(message);
    this.name = 'WorkoutAssignmentRequestError';
    this.fields = fields;
  }
}

const DATE_FIELDS: readonly WorkoutAssignmentDateField[] = [
  'previous_training_date',
  'current_training_date',
  'expected_delivery_date',
  'delivered_date',
];

function readDateFields(value: unknown): Partial<Record<WorkoutAssignmentDateField, string>> {
  if (!value || typeof value !== 'object') {
    return {};
  }

  const fields: Partial<Record<WorkoutAssignmentDateField, string>> = {};
  for (const field of DATE_FIELDS) {
    const message = (value as Record<string, unknown>)[field];
    if (typeof message === 'string' && message.trim()) {
      fields[field] = message;
    }
  }

  return fields;
}

export type AssignmentWorkoutStatus =
  | 'awaiting_fup_response'
  | 'ready_for_workout'
  | 'scheduled'
  | 'delivered'
  | 'canceled';

export type CopyFupLinkResult = {
  token: string;
  student_name_slug: string;
  assignment_id: number;
  attempt_number: number;
  created_at: string;
  reused_existing: boolean;
};

class WorkoutAssignmentServiceClass {
  private async post<T>(payload: Record<string, unknown>): Promise<T> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    const response = await fetch(`${config.supabaseFunctionsUrl}/manage-workout-assignment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
      body: JSON.stringify(payload),
    });

    const body = await response.json().catch(() => ({} as {
      success?: boolean;
      error?: string;
      fields?: unknown;
    }));

    if (!response.ok || body?.success === false) {
      const fields = readDateFields(body?.fields);
      throw new WorkoutAssignmentRequestError(
        body?.error || 'Não foi possível processar o ciclo de treino.',
        fields,
      );
    }

    return body as T;
  }

  async schedule(params: {
    assignmentId?: number | null;
    studentId: number;
    expectedDeliveryDate: string;
    notes?: string | null;
    numberOfCompetences?: number;
  }): Promise<void> {
    await this.post({
      action: 'schedule',
      assignment_id: params.assignmentId ?? null,
      student_id: params.studentId,
      expected_delivery_date: params.expectedDeliveryDate,
      ...(params.notes !== undefined ? { notes: params.notes } : {}),
      ...(params.numberOfCompetences ? { number_of_competences: params.numberOfCompetences } : {}),
    });
  }

  async getLatestWorkoutCodeFromHistory(studentId: number): Promise<string> {
    const { data, error } = await supabase
      .from('workout_assignments')
      .select('next_training_code')
      .eq('student_id', studentId)
      .eq('status', 'delivered')
      .not('delivered_at', 'is', null)
      .order('delivered_at', { ascending: false })
      .order('id', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      throw new Error(error.message || 'Não foi possível carregar o treino anterior.');
    }

    const code = typeof data?.next_training_code === 'string' ? data.next_training_code.trim() : '';
    return code ? code.toUpperCase() : '';
  }

  async createPerformanceAssignment(params: {
    studentId: number;
    previousTrainingCode?: string | null;
    previousTrainingDate?: string | null;
    currentTrainingCode: string;
    currentTrainingDate: string;
    fupAnswers?: {
      lastCycleFeedback: string;
      nextCycleAdjustments: string;
      weeklyAvailability: string;
      availableTrainingTime: string;
      additionalNotes: string;
    };
  }): Promise<void> {
    return this.post<void>({
      action: 'create-performance-assignment',
      student_id: params.studentId,
      previous_training_code: params.previousTrainingCode?.trim() || null,
      previous_training_date: params.previousTrainingDate || null,
      next_training_code: params.currentTrainingCode.trim(),
      current_training_date: params.currentTrainingDate,
      ...(params.fupAnswers ? { answers: params.fupAnswers } : {}),
    });
  }

  async updatePerformanceAssignment(params: {
    assignmentId: number | null;
    historyId: number;
    studentId: number;
    competence: string;
    previousTrainingCode: string | null;
    nextTrainingCode: string;
    expectedDeliveryDate: string;
    status: 'scheduled' | 'delivered';
    lastWorkoutDate: string;
    deliveredDate: string;
    deliveredAt?: string;
    deliveredByPortalUserId: number;
  }): Promise<void> {
    await this.post({
      action: 'update-performance-assignment',
      assignment_id: params.assignmentId,
      history_id: params.historyId,
      student_id: params.studentId,
      competence: params.competence,
      previous_training_code: params.previousTrainingCode,
      next_training_code: params.nextTrainingCode,
      expected_delivery_date: params.expectedDeliveryDate,
      status: params.status,
      last_workout_date: params.lastWorkoutDate,
      delivered_date: params.deliveredDate,
      ...(params.deliveredAt ? { delivered_at: params.deliveredAt } : {}),
      delivered_by_portal_user_id: params.deliveredByPortalUserId,
    });
  }

  async copyFupLink(params: {
    assignmentId: number | null;
    studentId: number;
    competence: string;
  }): Promise<CopyFupLinkResult> {
    return this.post<CopyFupLinkResult>({
      action: 'copy-fup-link',
      assignment_id: params.assignmentId,
      student_id: params.studentId,
      competence: params.competence,
    });
  }

  async removeFupLink(token: string): Promise<void> {
    const normalizedToken = token.trim();
    if (!normalizedToken) {
      throw new Error('token é obrigatório.');
    }

    await this.post({
      action: 'remove-fup-link',
      token: normalizedToken,
    });
  }

  async updateFupResponse(params: {
    studentFupId: number;
    answers?: FupStoredAnswers | {
      format?: 'legacy' | 'current';
      lastCycleFeedback?: string;
      nextCycleAdjustments?: string;
      weeklyAvailability?: string;
      availableTrainingTime?: string;
      additionalNotes?: string;
      trainingLocation?: string;
      hasPainOrLimitation?: boolean | null;
      painOrLimitationDetails?: string;
    };
    response?: string;
  }): Promise<{ response: string; answers: FupStoredAnswers | null }> {
    return this.post({
      action: 'update-fup',
      student_fup_id: params.studentFupId,
      ...(params.answers ? { answers: params.answers } : {}),
      ...(params.response !== undefined ? { response: params.response } : {}),
    });
  }

  async deleteFup(studentFupId: number): Promise<void> {
    await this.post({
      action: 'delete-fup',
      student_fup_id: studentFupId,
    });
  }

  async getCycleObservations(params: {
    studentId: number;
    studentFupId?: number | null;
    assignmentId?: number | null;
  }): Promise<CycleObservations> {
    const body = await this.post<CycleObservations & { success?: boolean }>({
      action: 'get-cycle-observations',
      student_id: params.studentId,
      student_fup_id: params.studentFupId ?? undefined,
      assignment_id: params.assignmentId ?? undefined,
    });

    return {
      anamnesis_ai_summary: body.anamnesis_ai_summary ?? null,
      anamnesis_goal_details: body.anamnesis_goal_details ?? null,
      anamnesis_exercise_history: body.anamnesis_exercise_history ?? null,
      fup_response: body.fup_response ?? null,
      fup_answers: body.fup_answers ?? null,
      fup_summary: body.fup_summary ?? null,
      fup_created_at: body.fup_created_at ?? null,
    };
  }

  async savePerformanceContext(assignmentId: number, nextTrainingCode?: string, note?: string): Promise<void> {
    await this.post({
      action: 'save-performance-context',
      assignment_id: assignmentId,
      next_training_code: nextTrainingCode,
      note,
    });
  }

  async updateDeliveryManagement(params: {
    assignmentId?: number | null;
    studentId: number;
    currentTrainingCode?: string | null;
    nextTrainingCode?: string | null;
    expectedDeliveryDate?: string | null;
    deliveredDate?: string | null;
    note?: string;
  }): Promise<{
    assignment_id: number;
    student_id: number;
    current_training_code: string | null;
    next_training_code: string | null;
    expected_delivery_date: string | null;
    delivered_at: string | null;
  }> {
    const body = await this.post<{
      data?: {
        assignment_id: number;
        student_id: number;
        current_training_code: string | null;
        next_training_code: string | null;
        expected_delivery_date: string | null;
        delivered_at: string | null;
      };
    }>({
      action: 'update-delivery-management',
      student_id: params.studentId,
      ...(params.assignmentId ? { assignment_id: params.assignmentId } : {}),
      current_training_code: params.currentTrainingCode ?? null,
      next_training_code: params.nextTrainingCode ?? null,
      expected_delivery_date: params.expectedDeliveryDate ?? null,
      ...(params.deliveredDate ? { delivered_date: params.deliveredDate } : { delivered_date: null }),
      ...(params.note ? { note: params.note } : {}),
    });

    return body.data ?? {
      assignment_id: params.assignmentId ?? 0,
      student_id: params.studentId,
      current_training_code: params.currentTrainingCode ?? null,
      next_training_code: params.nextTrainingCode ?? null,
      expected_delivery_date: params.expectedDeliveryDate ?? null,
      delivered_at: params.deliveredDate ?? null,
    };
  }

  async updatePerformanceCycle(params: {
    assignmentId: number;
    previousTrainingCode: string | null;
    nextTrainingCode: string | null;
    expectedDeliveryDate: string;
    deliveredDate?: string;
    deliveredAt?: string;
  }): Promise<void> {
    await this.post({
      action: 'update-performance-cycle',
      assignment_id: params.assignmentId,
      previous_training_code: params.previousTrainingCode,
      next_training_code: params.nextTrainingCode,
      expected_delivery_date: params.expectedDeliveryDate,
      ...(params.deliveredDate ? { delivered_date: params.deliveredDate } : {}),
      ...(params.deliveredAt ? { delivered_at: params.deliveredAt } : {}),
    });
  }

  async deliver(params: {
    assignmentId: number;
    nextTrainingCode?: string;
    previousTrainingCode?: string;
    note?: string;
    deliveredDate?: string;
    sourceTrainingId?: number;
  }): Promise<void> {
    await this.post({
      action: 'deliver',
      assignment_id: params.assignmentId,
      ...(params.nextTrainingCode ? { next_training_code: params.nextTrainingCode } : {}),
      ...(params.previousTrainingCode ? { previous_training_code: params.previousTrainingCode } : {}),
      ...(params.note ? { note: params.note } : {}),
      ...(params.deliveredDate ? { delivered_date: params.deliveredDate } : {}),
      ...(params.sourceTrainingId ? { source_training_id: params.sourceTrainingId } : {}),
    });
  }

  async saveNotes(params: {
    studentId: number;
    note: string;
  }): Promise<void> {
    await this.post({
      action: 'save-notes',
      student_id: params.studentId,
      note: params.note,
    });
  }

  async listStudentNotes(studentId: number): Promise<{
    notes: StudentNoteItem[];
    summary: string | null;
  }> {
    const body = await this.post<{ notes?: StudentNoteItem[]; notes_summary?: string | null }>({
      action: 'list-student-notes',
      student_id: studentId,
    });

    return {
      notes: Array.isArray(body.notes) ? body.notes : [],
      summary: typeof body.notes_summary === 'string' ? body.notes_summary : null,
    };
  }

  async deleteNote(noteId: number): Promise<void> {
    await this.post({
      action: 'delete-note',
      note_id: noteId,
    });
  }

  async updateAssignmentNotes(params: {
    assignmentId: number | null;
    studentId: number;
    competence: string;
    notes: string;
  }): Promise<{
    notes: string | null;
    assignmentId: number;
    workoutStatus: AssignmentWorkoutStatus | null;
  }> {
    const body = await this.post<{
      notes?: string | null;
      assignment_id?: number;
      workout_status?: string | null;
    }>({
      action: 'update-assignment-notes',
      assignment_id: params.assignmentId,
      student_id: params.studentId,
      competence: params.competence,
      notes: params.notes,
    });

    const assignmentId = Number(body.assignment_id);
    if (!Number.isInteger(assignmentId) || assignmentId <= 0) {
      throw new Error('O ciclo de treino foi salvo sem um identificador válido.');
    }

    const notes = typeof body.notes === 'string'
      ? body.notes
      : (body.notes === null ? null : params.notes.trim() || null);
    const workoutStatus = body.workout_status;

    return {
      notes,
      assignmentId,
      workoutStatus: workoutStatus === 'awaiting_fup_response'
        || workoutStatus === 'ready_for_workout'
        || workoutStatus === 'scheduled'
        || workoutStatus === 'delivered'
        || workoutStatus === 'canceled'
        ? workoutStatus
        : null,
    };
  }

  async insertWorkoutHistory(params: {
    studentId: number;
    competence: string;
    workoutCode: string;
    lastWorkoutDate: string;
    deliveredDate: string;
    deliveredByPortalUserId: number;
  }): Promise<void> {
    await this.post({
      action: 'insert-workout-history',
      student_id: params.studentId,
      competence: params.competence,
      workout_code: params.workoutCode,
      last_workout_date: params.lastWorkoutDate,
      delivered_date: params.deliveredDate,
      delivered_by_portal_user_id: params.deliveredByPortalUserId,
    });
  }

  async updateWorkoutHistory(params: {
    historyId: number;
    studentId: number;
    competence: string;
    workoutCode: string;
    lastWorkoutDate: string;
    deliveredDate: string;
    deliveredByPortalUserId: number;
  }): Promise<void> {
    await this.post({
      action: 'update-workout-history',
      history_id: params.historyId,
      student_id: params.studentId,
      competence: params.competence,
      workout_code: params.workoutCode,
      last_workout_date: params.lastWorkoutDate,
      delivered_date: params.deliveredDate,
      delivered_by_portal_user_id: params.deliveredByPortalUserId,
    });
  }

  async deleteWorkoutHistory(params: {
    historyId: number;
    studentId: number;
  }): Promise<void> {
    await this.post({
      action: 'delete-workout-history',
      history_id: params.historyId,
      student_id: params.studentId,
    });
  }

  async deleteWorkoutAssignment(params: {
    assignmentId: number;
    studentId: number;
  }): Promise<void> {
    await this.post({
      action: 'delete-workout-assignment',
      assignment_id: params.assignmentId,
      student_id: params.studentId,
    });
  }

  async insertMariContact(params: {
    studentId: number;
    contactedAt: string;
  }): Promise<void> {
    await this.post({
      action: 'insert-mari-contact',
      student_id: params.studentId,
      contacted_at: params.contactedAt,
    });
  }

  async updateMariContact(params: {
    contactId: number;
    studentId: number;
    contactedAt: string;
  }): Promise<void> {
    await this.post({
      action: 'update-mari-contact',
      contact_id: params.contactId,
      student_id: params.studentId,
      contacted_at: params.contactedAt,
    });
  }

  async deleteMariContact(params: {
    contactId: number;
    studentId: number;
  }): Promise<void> {
    await this.post({
      action: 'delete-mari-contact',
      contact_id: params.contactId,
      student_id: params.studentId,
    });
  }
}

export const WorkoutAssignmentService = new WorkoutAssignmentServiceClass();
